import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { SituacaoTour } from '../../models/Tour.model';
import UsuarioService from '../../services/Usuario.service';

import GuidedTour, { GuidedTourStep } from './GuidedTour';

interface TourRoutes {
  home: string;
  users: string;
  transactions: string;
  postpaid: string;
}

interface TourStep {
  route: string;
  selector: string;
  title: string;
  description: string;
}

interface PjTourContextValue {
  startTour: (route?: string) => void;
}

const PjTourContext = createContext<PjTourContextValue | null>(null);

// Seletores separados por vírgula valem em ordem de prioridade: o primeiro visível vence.
export const findVisibleTarget = (selector: string) =>
  selector.split(',').reduce<HTMLElement | null>(
    (found, item) =>
      found ??
      Array.from(document.querySelectorAll<HTMLElement>(item.trim())).find((element) => {
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      }) ??
      null,
    null
  );

export const PjGuidedTourProvider = ({
  children,
  routes
}: PropsWithChildren<{ routes: TourRoutes }>) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeRoute, setActiveRoute] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [target, setTarget] = useState<HTMLElement | null>(null);

  const steps = useMemo<TourStep[]>(
    () => [
      {
        route: routes.home,
        selector: '[data-tour-pj="wallet-recharge"]',
        title: 'Recarregar saldo',
        description:
          'Recarregue aqui a Carteira da empresa. É desse saldo que saem as transferências para os funcionários.'
      },
      {
        route: routes.home,
        selector: '[data-tour-pj="wallet-filter"]',
        title: 'Filtrar o extrato',
        description:
          'Filtre o extrato por período para achar uma movimentação. Por aqui você também exporta o extrato em CSV.'
      },
      {
        route: routes.home,
        selector: '[data-tour-pj="wallet-transactions"]',
        title: 'Extrato da Carteira',
        description:
          'Tudo o que entra e sai da Carteira aparece aqui. Nas recargas, clique em "Ver recibo" para abrir o comprovante.'
      },
      {
        route: routes.home,
        selector: '[data-tour-pj="account"], [data-tour-pj="menu"]',
        title: 'Dados da empresa',
        description: 'Para ver ou alterar os dados da empresa, acesse seu perfil por aqui.'
      },
      {
        route: routes.users,
        selector: '[data-tour-pj="associate-user"]',
        title: 'Associar usuário',
        description:
          'Adicione funcionários à conta da empresa. Depois de associados, eles já podem receber saldo da Carteira.'
      },
      {
        route: routes.users,
        selector: '[data-tour-pj="associated-users"]',
        title: 'Funcionários associados',
        description:
          'Esta é a lista dos funcionários associados, com o saldo de cada um. A chave abaixo do saldo ativa ou desativa o funcionário, mas só pode ser usada quando o saldo está zerado e o cadastro dele está completo.'
      },
      {
        route: routes.users,
        selector: '[data-tour-pj="transfer-balance"]',
        title: 'Transferir ou estornar saldo',
        description:
          'Marque um ou mais funcionários na lista e este botão é liberado. Com ele você envia saldo ou estorna o valor de volta para a Carteira.'
      },
      {
        route: routes.transactions,
        selector: '[data-tour-pj="employee-filter"]',
        title: 'Filtrar transações',
        description:
          'Use os filtros para encontrar a movimentação de um funcionário sem precisar rolar a lista toda.'
      },
      {
        route: routes.transactions,
        selector: '[data-tour-pj="employee-transactions"]',
        title: 'Movimentações dos funcionários',
        description:
          'Cada linha é uma movimentação feita por um funcionário. Nas compras de passagem, clique em "Ver detalhes" para ver a viagem. Nas recargas, clique em "Ver recibo".'
      },
      {
        route: routes.postpaid,
        selector: '[data-tour-pj="postpaid"], [data-tour-pj="postpaid-title"]',
        title: 'Consumo pós-pago',
        description:
          'Aqui você solicita o pós-pago para a empresa ou, se já tiver, acompanha o consumo.'
      }
    ],
    [routes.home, routes.users, routes.transactions, routes.postpaid]
  );

  const routeSteps = useMemo(
    () => steps.filter((step) => step.route === activeRoute),
    [steps, activeRoute]
  );

  const startTour = useCallback(
    (route?: string) => {
      const requestedRoute = route ?? location.pathname;
      const tourRoute = steps.some((step) => step.route === requestedRoute)
        ? requestedRoute
        : routes.home;
      setTarget(null);
      setActiveIndex(0);
      setActiveRoute(tourRoute);
      if (location.pathname !== tourRoute) navigate(tourRoute);
    },
    [location.pathname, navigate, routes.home, steps]
  );

  const changeStep = (nextIndex: number) => {
    setTarget(null);
    setActiveIndex(nextIndex);
  };

  const closeTour = (situacao: SituacaoTour.Concluido | SituacaoTour.Dispensado) => {
    setTarget(null);
    setActiveRoute(null);
    void UsuarioService.finalizarTour(situacao).catch(() => undefined);
  };

  useLayoutEffect(() => {
    const step = routeSteps[activeIndex];
    if (!step || location.pathname !== activeRoute) {
      setTarget(null);
      return undefined;
    }

    const updateTarget = () => setTarget(findVisibleTarget(step.selector));
    updateTarget();

    const observer = new MutationObserver(updateTarget);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', updateTarget);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateTarget);
    };
  }, [activeIndex, activeRoute, location.pathname, routeSteps]);

  const routeLabels: Record<string, string> = {
    [routes.home]: 'Carteira',
    [routes.users]: 'Gestão de usuários',
    [routes.transactions]: 'Transações de usuários',
    [routes.postpaid]: 'Consumo pós-pago'
  };

  const guidedSteps: GuidedTourStep[] = routeSteps.map((step, index) => ({
    title: step.title,
    description: step.description,
    target: { current: index === activeIndex ? target : null }
  }));

  return (
    <PjTourContext.Provider value={{ startTour }}>
      {children}
      <GuidedTour
        open={activeRoute !== null}
        steps={guidedSteps}
        activeStep={activeIndex}
        label={activeRoute ? routeLabels[activeRoute] : undefined}
        onStepChange={changeStep}
        onClose={() => closeTour(SituacaoTour.Dispensado)}
        onComplete={() => closeTour(SituacaoTour.Concluido)}
      />
    </PjTourContext.Provider>
  );
};

export const usePjGuidedTour = () => {
  const context = useContext(PjTourContext);
  if (!context) throw new Error('usePjGuidedTour must be used inside PjGuidedTourProvider');
  return context;
};
